import { SSCarouselResult, ProductNative } from '../search-spring/types/types'
import { PRICE_ON_APPLICATION_LABEL, formatDollars, isPriceOnApplication } from '../helpers/formatMoney'
import { type_GM_STATE } from '../helpers'

declare var GM_STATE: type_GM_STATE

/*
  The single shape every carousel card renders. Searchspring results, native
  Shopify product JSON and manually selected products are all mapped onto it,
  so the price, "FROM" and price-on-application rules live in one place.
*/
export type CarouselProduct = {
  id: string
  title: string
  url: string
  image: string
  imageHover?: string
  priceText: string
  compareAtText?: string
  biLine?: string
  /* Only set for Searchspring results - the click beacons need the raw result. */
  raw?: SSCarouselResult
}

const withLocale = (url: string) => {
  const rootUrlWithoutSlash = window.routes?.rootUrlWithoutSlash

  return !rootUrlWithoutSlash || url.includes(rootUrlWithoutSlash) ? url : rootUrlWithoutSlash + url
}

const priceText = (amount: number, hasFromLabel: boolean, tags: string[]) =>
  isPriceOnApplication(amount, tags)
    ? PRICE_ON_APPLICATION_LABEL
    : `${hasFromLabel ? 'FROM ' : ''}${formatDollars(amount)}`

export const fromSearchspring = (product: SSCarouselResult): CarouselProduct => {
  const { defaultImage } = GM_STATE.shopify
  const { core } = product.mappings
  const { ss_tags, tags } = product.attributes
  const hasFromLabel = ss_tags.includes('FROM') || ss_tags.includes('from') || tags.includes('Template_Same Pair')
  const price = Number(core.price)
  const showCompareAtPrice = Number(core.msrp) > price && !isPriceOnApplication(price, tags)

  return {
    id: product.id,
    title: core.name,
    url: withLocale(core.url),
    image: `${product.attributes.image || defaultImage}&width=1200`,
    imageHover: product.attributes.ss_image_hover,
    priceText: priceText(price, hasFromLabel, tags),
    compareAtText: showCompareAtPrice ? formatDollars(Number(core.msrp)) : undefined,
    biLine: product.attributes.mfield_custom_product_bi_line || undefined,
    raw: product
  }
}

export const fromNative = (product: ProductNative): CarouselProduct => {
  const { defaultImage } = GM_STATE.shopify
  const { tags } = product
  const variesByPrice = product.price_max !== product.price_min
  const hasFromLabel =
    variesByPrice || tags.includes('FROM') || tags.includes('from') || tags.includes('Template_Same Pair')
  /* Native prices are in cents. */
  const price = Number(variesByPrice ? product.price_min : product.price) / 100
  const compareAt = Number(product.compareAtPriceMin) / 100
  const showCompareAtPrice = compareAt > price && !isPriceOnApplication(price, tags)

  return {
    id: String(product.id),
    title: product.title,
    url: withLocale(product.url),
    image: `${product.featured_image || defaultImage}&width=1200`,
    imageHover: product.images.length > 1 ? product.images[1] : undefined,
    priceText: priceText(price, hasFromLabel, tags),
    compareAtText: showCompareAtPrice ? formatDollars(compareAt) : undefined,
    biLine: product.metafields.productBiLine || undefined
  }
}
